import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { type Product, type ProductPeriod, type ProductOption } from '../types/product';
import { type PriceCalculation } from '../api/client';

export interface StoredPlanDetails {
  product_id: number;
  disk_space: number;
  bandwidth: number;
  users_count: number;
  period_id: number;
  selected_options: Array<{ type: string; value: number }>;
  // Metadata for UI restoration
  product_name: string;
  period_name: string;
  initial_calculation: {
    base_price: number;
    option_costs: { [key: string]: number };
    period_discount: number;
    subtotal: number;
    vat_rate: string;
    vat_amount: number;
    total: number;
    currency: string;
    includes_vat: boolean;
  } | null;
}

interface PlanContextType {
  selectedPlan: StoredPlanDetails | null;
  setSelectedPlan: (plan: StoredPlanDetails | null) => void;
  savePlanDetails: (
    product: Product,
    period: ProductPeriod,
    diskSpace: ProductOption | null,
    users: ProductOption | null,
    priceCalculation: PriceCalculation | null
  ) => void;
  clearPlanDetails: () => void;
}

const STORAGE_KEY = 'diskray_selected_plan';

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export function PlanProvider({ children }: { children: React.ReactNode }) {
  const [selectedPlan, setSelectedPlan] = useState<StoredPlanDetails | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  });

  // Persist plan details to localStorage
  useEffect(() => {
    const storedPlan = localStorage.getItem(STORAGE_KEY);
    const currentPlan = selectedPlan ? JSON.stringify(selectedPlan) : null;
    
    // Only update if the plans are different
    if (storedPlan !== currentPlan) {
      if (selectedPlan) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedPlan));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [selectedPlan]);

  const savePlanDetails = React.useCallback(
    (
      product: Product,
      period: ProductPeriod,
      diskSpace: ProductOption | null,
      users: ProductOption | null,
      priceCalculation: PriceCalculation | null
    ) => {
      const planDetails: StoredPlanDetails = {
        product_id: product.id,
        disk_space: diskSpace?.value || product.disk_space,
        bandwidth: (diskSpace?.value || product.disk_space) * 2,
        users_count: users?.value || product.allowed_users,
        period_id: period.id,
        selected_options: [
          ...(diskSpace ? [{ type: 'disk_space', value: diskSpace.value }] : []),
          ...(users ? [{ type: 'users', value: users.value }] : [])
        ],
        // Metadata
        product_name: product.name,
        period_name: period.name,
        initial_calculation: priceCalculation ? {
          base_price: priceCalculation.base_price,
          option_costs: priceCalculation.option_costs,
          period_discount: priceCalculation.period_discount,
          subtotal: priceCalculation.subtotal,
          vat_rate: priceCalculation.vat_rate,
          vat_amount: priceCalculation.vat_amount,
          total: priceCalculation.total,
          currency: priceCalculation.currency,
          includes_vat: priceCalculation.includes_vat
        } : null
      };

      // Only update if the plan details have actually changed
      setSelectedPlan(prev => {
        if (JSON.stringify(prev) === JSON.stringify(planDetails)) {
          return prev;
        }
        return planDetails;
      });
    },
    []
  );

  const clearPlanDetails = React.useCallback(() => {
    setSelectedPlan(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Track location changes
  const location = useLocation();

  // Clear plan details only after successful payment or when explicitly leaving the payment flow
  useEffect(() => {
    const shouldKeepPlan = (path: string) => {
      // Keep plan during initial selection, auth, and payment
      const keepPlanPaths = [
        '/auth/register',
        '/auth/login',
        '/auth/verify-email',
        '/auth/oauth-callback',
        '/auth/plans',
        '/pricing',
        '/dashboard/payments/new/payment'
      ];

      // Check if path is in allowed paths
      return keepPlanPaths.some(allowedPath => path.startsWith(allowedPath));
    };

    // Clear plan if navigating away from payment flow
    if (!shouldKeepPlan(location.pathname)) {
      clearPlanDetails();
    }

    // Handle page unload
    const handleBeforeUnload = () => {
      if (!shouldKeepPlan(location.pathname)) {
        clearPlanDetails();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname, clearPlanDetails]);

  return (
    <PlanContext.Provider value={{
      selectedPlan,
      setSelectedPlan,
      savePlanDetails,
      clearPlanDetails
    }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const context = useContext(PlanContext);
  if (context === undefined) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
}
